<script setup lang="ts">
import { computed, ref, watchEffect } from "vue";
import { useI18n } from "vue-i18n";
import debounce from "lodash-es/debounce";

import type {
  ManufacturerType,
  ManufacturerPayloadType,
} from "@/common/types/manufacturer";

import { baseInput, baseSelect, baseTextEditor } from "@/common/components";

const { t } = useI18n();

const emit = defineEmits(["update:data", "salve:data"]);

const props = defineProps<{
  data?: ManufacturerType | null;
  isPatch?: boolean;
}>();

const DEFAULT_DATA = {
  owner_id: props.data?.manufacturer_id,
  name: "",
  web_site: "",
  status: "ACTIVE",
  description: "",
};

const payload = ref<ManufacturerPayloadType>({ ...DEFAULT_DATA });

const optionsStatus = computed(() => [
  {
    option: t("common.labels.active"),
    value: "ACTIVE",
  },
  {
    option: t("common.labels.inactive"),
    value: "INACTIVE",
  },
]);

function update(key: keyof typeof payload.value) {
  if (props.isPatch) {
    emit("update:data", { [key]: payload.value[key] });
    return;
  }

  emit("update:data", payload.value);
}

function resumeData() {
  if (!props.data) return;

  const { name, web_site, status, description } = props.data;

  payload.value = {
    ...payload.value,
    name,
    web_site,
    status,
    description,
  };
}

const debouceUpdate = debounce(update, 300);

watchEffect(() => {
  resumeData();
});
</script>

<template>
  <section class="form-manufacturer">
    <base-input
      v-model="payload.name"
      :value="payload.name"
      :placeholder="t('common.placeholders.name')"
      required
      @update:modelValue="debouceUpdate('name')"
    >
      <template #label> {{ t("common.labels.name") }} * </template>
    </base-input>

    <base-input
      v-model="payload.web_site"
      :value="payload.web_site"
      placeholder="exe: https://www.website.com"
      @update:modelValue="debouceUpdate('web_site')"
    >
      <template #label> {{ t("common.labels.web_site") }}</template>
    </base-input>

    <base-select
      v-model="payload.status"
      :value="payload.status"
      :options="optionsStatus"
      :is-empty="false"
      @change="debouceUpdate('status')"
    >
      <template #label> {{ t("common.labels.status") }}</template>
    </base-select>

    <base-text-editor
      v-model="payload.description"
      @update:modelValue="debouceUpdate('description')"
    >
      <template #label> {{ t("common.labels.description") }}</template>
    </base-text-editor>
  </section>
</template>

<style lang="scss" scoped>
.form-manufacturer {
  display: flex;
  flex-direction: column;
  width: 100%;
  gap: 1rem;
}
</style>
